import {DateTime} from 'luxon';
import {fromDatetimeHelper} from '../../../../../core/helpers/from-datetime.helper';

export class VehicleSearch {
  constructor(
    public readonly id: number,
    public readonly brand: string,
    public readonly model: string,
    public readonly category: string,
    public readonly vin: string | null,
    public readonly registrationPlate: string | null,
    public readonly factoryNumber: string | null,
    public readonly storageName: string,
    public readonly ownerName: string,
    public readonly returningPerson: string,
    public readonly returnDate: DateTime | null,
    public readonly releaseDate: DateTime | null,
    public readonly receiver: string | null,
    public readonly keyNumber: number | null,
    public readonly documentNumber: string,
    public readonly archiveNumber: string | null
  ) {
  }

  get displayName(): string {
    return `${this.brand} ${this.model}`;
  }

  get fullName(): string {
    return this.registrationPlate
      ? `${this.displayName} (${this.registrationPlate})`
      : this.displayName;
  }

  get displayNameWithVin(): string {
    return `${this.displayName} - VIN: ${this.vin}`;
  }

  get displayIdentificationField(): string {
    return this.vin ? `VIN: ${this.vin}` : `SN: ${this.factoryNumber}`;
  }

  get returnDateDisplay(): string | null {
    return this.returnDate?.toFormat('dd.MM.yyyy') ?? null;
  }

  get releaseDateDisplay(): string | null {
    return this.releaseDate?.toFormat('dd.MM.yyyy') ?? null;
  }

  get displayReturnDate(): string {
    return fromDatetimeHelper(this.returnDate) || '-';
  }

  get displayReleaseDate(): string {
    return fromDatetimeHelper(this.releaseDate) || '-';
  }

  get returnDateISO(): string | null {
    return this.returnDate?.toISODate() ?? null;
  }

  get releaseDateISO(): string | null {
    return this.releaseDate?.toISODate() ?? null;
  }

  get returnDateRelative(): string | null {
    if (!this.returnDate) return null;

    const now = DateTime.now();
    const diff = this.returnDate.diff(now, ['days']).days;

    if (Math.abs(diff) < 1) return 'dzisiaj';
    if (diff < 0) return `${Math.abs(Math.floor(diff))} dni temu`;
    return `za ${Math.floor(diff)} dni`;
  }

  get releaseDateRelative(): string | null {
    if (!this.releaseDate) return null;

    const now = DateTime.now();
    const diff = this.releaseDate.diff(now, ['days']).days;

    if (Math.abs(diff) < 1) return 'dzisiaj';
    if (diff < 0) return `${Math.abs(Math.floor(diff))} dni temu`;
    return `za ${Math.floor(diff)} dni`;
  }

  get daysSinceReturn(): number | null {
    if (!this.returnDate) return null;
    return Math.floor(DateTime.now().diff(this.returnDate, ['days']).days);
  }

  get daysSinceRelease(): number | null {
    if (!this.releaseDate) return null;
    return Math.floor(DateTime.now().diff(this.releaseDate, ['days']).days);
  }

  get isInStorage(): boolean {
    return this.returnDate !== null && this.releaseDate === null;
  }

  get wasReturned(): boolean {
    return this.returnDate !== null;
  }

  get wasReleased(): boolean {
    return this.releaseDate !== null;
  }

  get isCompleted(): boolean {
    return this.wasReturned && this.wasReleased;
  }

  get isPending(): boolean {
    return !this.wasReturned;
  }

  get isReturnOverdue(): boolean {
    if (this.wasReturned) return false;
    if (!this.returnDate) return false;
    return this.returnDate < DateTime.now();
  }

  get isReleaseOverdue(): boolean {
    if (this.wasReleased) return false;
    if (!this.releaseDate) return false;
    return this.releaseDate < DateTime.now();
  }

  get hasKey(): boolean {
    return this.keyNumber !== null;
  }

  get keyNumberDisplay(): string {
    return this.hasKey ? `Klucz #${this.keyNumber}` : 'Brak klucza';
  }

  get documentNumberDisplay(): string {
    return `Dokument #${this.documentNumber}`;
  }

  get archiveNumberDisplay(): string {
    return `Archiwum #${this.archiveNumber}`;
  }

  get hasReturningPerson(): boolean {
    return this.returningPerson.trim().length > 0;
  }

  get hasReceiver(): boolean {
    return ((this.receiver ?? '').trim().length) > 0;
  }

  get returningPersonDisplay(): string {
    return this.hasReturningPerson
      ? `Zwrócił: ${this.returningPerson}`
      : 'Brak danych';
  }

  get receiverDisplay(): string {
    return this.hasReceiver
      ? `Odbiorca: ${this.receiver}`
      : 'Brak danych';
  }

  get storageNameDisplay(): string {
    return this.storageName.trim().length > 0
      ? `Magazyn: ${this.storageName}`
      : 'Brak magazynu';
  }

  get hasStorage(): boolean {
    return this.storageName.trim().length > 0;
  }

  get statusLabel(): string {
    if (this.isCompleted) return 'Wydany';
    if (this.isInStorage) return 'Magazyn';
    return 'Nieznany';
  }

  get statusColor(): string {
    if (this.isCompleted) return 'green';
    if (this.isInStorage) return 'blue';
    if (this.isPending) return 'orange';
    return 'gray';
  }

  get statusIcon(): string {
    if (this.isCompleted) return 'pi-check';
    if (this.isInStorage) return 'pi-warehouse';
    if (this.isPending) return 'pi-clock';
    return 'pi-question';
  }

  get hasWarnings(): boolean {
    return this.isReturnOverdue || this.isReleaseOverdue || !this.hasKey;
  }

  get warningMessages(): string[] {
    const warnings: string[] = [];

    if (this.isReturnOverdue) {
      warnings.push(`Zwrot przeterminowany o ${Math.abs(this.daysSinceReturn!)} dni!`);
    }

    if (this.isReleaseOverdue) {
      warnings.push(`Wydanie przeterminowane o ${Math.abs(this.daysSinceRelease!)} dni!`);
    }

    if (!this.hasKey) {
      warnings.push('Brak przypisanego klucza');
    }

    return warnings;
  }

  get primaryWarning(): string | null {
    const warnings = this.warningMessages;
    return warnings.length > 0 ? warnings[0] : null;
  }

  get searchText(): string {
    return [
      this.brand,
      this.model,
      this.vin,
      this.registrationPlate,
      this.factoryNumber,
      this.storageName,
      this.ownerName,
      this.returningPerson,
      this.receiver
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  matchesSearch(term: string): boolean {
    if (!term || term.trim().length === 0) return true;
    return this.searchText.includes(term.toLowerCase());
  }

  compareByReturnDate(other: VehicleSearch): number {
    if (!this.returnDate && !other.returnDate) return 0;
    if (!this.returnDate) return 1;
    if (!other.returnDate) return -1;
    return this.returnDate.toMillis() - other.returnDate.toMillis();
  }

  compareByReleaseDate(other: VehicleSearch): number {
    if (!this.releaseDate && !other.releaseDate) return 0;
    if (!this.releaseDate) return 1;
    if (!other.releaseDate) return -1;
    return this.releaseDate.toMillis() - other.releaseDate.toMillis();
  }

  compareByName(other: VehicleSearch): number {
    return this.displayName.localeCompare(other.displayName);
  }

  get displayVin(): string {
    return this.vin || 'Brak VIN';
  }

  get displayDocumentNumber(): string {
    return this.documentNumber || '-';
  }

  get displayFactoryNumber(): string {
    return this.factoryNumber || '-';
  }

  get displayKeyNumber(): string {
    return this.keyNumber?.toString() || '-';
  }

  get displayRegistrationPlate(): string {
    return this.registrationPlate || '-';
  }
}
